'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'
import { BadgeCheck, Info, MapPin, Search, ShieldAlert, TrendingDown } from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/homepage-october/ui/input-group'
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/homepage-october/ui/item'
import { ToggleGroup, ToggleGroupItem } from '@/components/homepage-october/ui/toggle-group'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { Container, IdChip, LiveDot, words } from '../blocks/layout'
import { Marquee } from '../blocks/Rail'
import { RotationControls, useSwipeSelection } from '../blocks/RotationControls'
import { useReducedMotion } from '../hooks'
import type { Model } from '../types'

/* ------------------------------------------------------------------ the live price-comparison card */
function CompareCard({ m }: { m: Model['hero'] }) {
  const [i, setI] = React.useState(0)
  const [paused, setPaused] = React.useState(false)
  const move = (direction: number) => { setPaused(true); setI((v) => (v + direction + m.featured.length) % m.featured.length) }
  const swipe = useSwipeSelection(move, () => setPaused(true))
  const card = React.useRef<HTMLDivElement>(null)
  const hold = React.useRef({ hover: false, focus: false, visible: false })
  const reduced = useReducedMotion()
  React.useEffect(() => {
    if (!card.current) return
    const observer = new IntersectionObserver(([entry]) => { hold.current.visible = entry.isIntersecting }, { threshold: 0.15 })
    observer.observe(card.current)
    return () => observer.disconnect()
  }, [])
  React.useEffect(() => {
    if (reduced || paused || m.featured.length < 2) return
    const t = window.setInterval(() => { if (hold.current.visible && !hold.current.hover && !hold.current.focus && !document.hidden) setI((x) => (x + 1) % m.featured.length) }, 6500)
    return () => window.clearInterval(t)
  }, [reduced, paused, m.featured.length])
  if (!m.featured.length) return null
  return (
    <Card
      ref={card}
      {...swipe}
      data-auto={6500}
      data-rotator="hero"
      data-paused={paused || reduced}
      className="group/hero relative gap-0! overflow-hidden py-0! shadow-[0_24px_60px_-28px_rgba(4,105,240,0.35)]! ring-1 ring-black/5"
      onMouseEnter={() => { hold.current.hover = true }}
      onMouseLeave={() => { hold.current.hover = false }}
      onFocusCapture={() => { hold.current.focus = true }}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) hold.current.focus = false }}
    >
      <div className={compactClassName("flex items-center justify-between gap-3 border-b bg-neutral-50/80 px-5 py-3")}>
        <Badge variant="secondary" className="gap-1.5 bg-white font-semibold ring-1 ring-black/5">
          <BadgeCheck aria-hidden="true" className="text-[#0469f0]" />
          {m.ladderLabel}
        </Badge>
        <span className={compactClassName("text-xs font-medium text-neutral-500")}>{m.bestLabel}</span>
      </div>
      {m.featured.map((f, k) => k === i ? (
        <div key={f.compare.href}>
          {/* Keyed to the switch, so every piece plays its entrance again each time this product comes round: the photo
              slides in and keeps floating, the facts rise one after another, the store rows follow, "Lowest" pops. */}
          <div key={k === i ? `on-${i}` : 'off'}>
            <div className={compactClassName("grid gap-5 p-5 sm:grid-cols-[150px_1fr] sm:items-center")}>
              <div data-motion className={compactClassName("mx-auto aspect-square w-40 animate-v3-slide overflow-hidden rounded-xl bg-white sm:w-full")}>
                <span data-motion className={compactClassName("block size-full")} style={{ animation: 'v3-float 6s ease-in-out 0.6s infinite' }}>
                  <Img src={f.img.src} alt={f.img.alt} eager={k === 0} fetchPriority={k === 0 ? 'high' : 'auto'} className="size-full" />
                </span>
              </div>
              <div className={compactClassName("min-w-0")}>
                <p data-motion className={compactClassName("animate-v3-rise text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500")}>{f.brand}</p>
                <p data-motion className={compactClassName("mt-1 animate-v3-rise text-pretty text-[15px] font-semibold leading-snug text-neutral-950 [animation-delay:60ms]")}>{f.title}</p>
                <p data-motion className={compactClassName("tabular mt-3 animate-v3-rise text-4xl font-bold tracking-tight text-neutral-950 [animation-delay:120ms]")}><span className={compactClassName("rotprice")}>{f.price}</span></p>
                <p data-motion className={compactClassName("mt-1.5 inline-flex animate-v3-rise items-center gap-1.5 text-sm font-medium text-[#067647] [animation-delay:180ms]")}>
                  <TrendingDown aria-hidden="true" className="size-4" />
                  {f.spread}
                </p>
                {f.fin || f.tracked ? (
                  <p data-motion className={compactClassName("mt-2 flex animate-v3-rise flex-wrap items-center gap-2 text-xs text-neutral-600 [animation-delay:240ms]")}>
                    {f.fin ? <IdChip>{f.fin}</IdChip> : null}
                    {f.tracked ? <span className={compactClassName("inline-flex items-center gap-1.5")}><LiveDot />{f.tracked}</span> : null}
                  </p>
                ) : null}
              </div>
            </div>
            <ul className={compactClassName("divide-y border-y")}>
              {f.rows.map((r, j) => (
                <li key={r.store} data-motion className={compactClassName(cn('flex animate-v3-rise items-center gap-3 px-5 py-2.5 text-sm', r.best && 'bg-[#f0faf4]'))} style={{ animationDelay: `${260 + j * 70}ms` }}>
                  <RetailerMark name={r.store} />
                  <span className={compactClassName("flex-1 font-medium text-neutral-800")}>{r.store}</span>
                  {r.best ? <Badge data-motion className="animate-v3-pop border-transparent bg-[#067647]! text-white! [animation-delay:600ms]">Lowest</Badge> : null}
                  <span className={compactClassName("tabular font-semibold text-neutral-950")} title={r.checked}>{r.price}</span>
                </li>
              ))}
            </ul>
            <div data-motion className={compactClassName("flex animate-v3-rise justify-end px-5 pt-4 [animation-delay:520ms]")}>
              <Button asChild variant="ctaSecondary" className="rounded-full!">
                <DestinationLink href={f.compare.href} aria-label={f.compare.aria}>{f.compare.text}<ArrowRight aria-hidden="true" /></DestinationLink>
              </Button>
            </div>
          </div>
        </div>
      ) : null)}
      <div className={compactClassName("v3-hero-controls flex flex-wrap items-center justify-between gap-3 px-5 pt-3 pb-5")}>
        <div className={compactClassName("flex items-center gap-3")}>
        <ToggleGroup type="single" value={String(i)} onValueChange={(v) => { if (v) { setI(Number(v)); setPaused(true) } }} aria-label={m.dotsAria} className="gap-2">
          {m.featured.map((f, k) => (
            <ToggleGroupItem
              key={f.fin || f.dot}
              value={String(k)}
              aria-label={f.dot}
              className="size-11! overflow-hidden rounded-lg! border bg-white p-1! data-[state=on]:border-[#0469f0] data-[state=on]:ring-2 data-[state=on]:ring-[#0469f0]/25"
            >
              <Img src={f.img.src} alt="" className="size-full" />
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {/* The 6.5 s until the next product, as a filling bar; it pauses with the rotation while the pointer or focus is on the card. */}
        {!reduced && !paused ? (
          <span aria-hidden="true" className={compactClassName("relative h-1 w-16 overflow-hidden rounded-full bg-neutral-200")}>
            <span key={i} data-motion className={compactClassName("absolute inset-y-0 left-0 w-full origin-left animate-v3-progress bg-[#0469f0] group-hover/hero:[animation-play-state:paused] group-focus-within/hero:[animation-play-state:paused]")} />
          </span>
        ) : null}
        </div>
        <RotationControls label="Featured products" paused={paused} reduced={reduced} onPause={() => setPaused((v) => !v)} onMove={move} />
      </div>
    </Card>
  )
}

/* ------------------------------------------------------------------ hero */
export function Hero({ m }: { m: Model['hero'] }) {
  return (
    <section aria-label={m.aria} data-v3="V02" className={compactClassName("relative overflow-hidden bg-white")}>
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
        <div className={compactClassName("absolute -top-40 -left-32 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.22),transparent_65%)]")} />
        <div className={compactClassName("absolute -top-24 right-[-120px] size-[560px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.14),transparent_65%)]")} />
        <div className={compactClassName("absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white")} />
      </div>
      <Container className="relative grid items-center gap-8 pt-6 pb-8 md:pt-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:pt-16 lg:pb-14">
        <div>
          <h1 className={compactClassName("mt-5 text-balance text-[2.6rem] font-bold leading-[1.02] tracking-tight text-neutral-950 sm:text-6xl lg:text-[4.1rem]")}>
            {m.h1[0]}
            <span className={compactClassName("block bg-gradient-to-r from-[#0469f0] to-[#004aad] bg-clip-text text-transparent")}>{m.h1[1]}</span>
          </h1>
          <p className={compactClassName("mt-5 max-w-xl text-pretty text-lg leading-relaxed text-neutral-700")}>
            <span className={compactClassName("hidden sm:inline")}>{m.desc}</span>
            <span className={compactClassName("sm:hidden")}>{m.descCompact}</span>
          </p>
          <p className={compactClassName("mt-3 max-w-xl text-sm text-neutral-600")}>{m.census}</p>
          <form action={m.search.action} method={m.search.method} role="search" className={compactClassName("mt-7 max-w-xl")}>
            <InputGroup className="h-14 rounded-full! border-neutral-300 bg-white pr-1.5 pl-2 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)]! has-[[data-slot=input-group-control]:focus-visible]:border-[#0469f0]!">
              <InputGroupAddon><Search aria-hidden="true" className="size-5 text-neutral-500" /></InputGroupAddon>
              <InputGroupInput type="search" name={m.search.name} placeholder={m.search.placeholder} aria-label={m.search.aria} className="text-base! shadow-none! focus-visible:ring-0!" />
              <InputGroupAddon align="inline-end">
                <InputGroupButton type="submit" variant="ctaPrimary" className="h-11! rounded-full! px-6! text-[15px]! font-semibold">{m.search.button}</InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </form>
          <div className={compactClassName("mt-5 flex flex-wrap items-center gap-2")}>
            <span className={compactClassName("mr-1 text-sm font-medium text-neutral-600")}>{m.popular.label}</span>
            {m.popular.links.map((l) => (
              <DestinationLink key={l.href} href={l.href} className="rounded-full border border-neutral-200 bg-white/80 px-3 py-1.5 text-[13px] font-medium text-neutral-800 transition hover:border-[#0469f0] hover:text-[#0454c0]">
                {l.text}
              </DestinationLink>
            ))}
          </div>
        </div>
        <CompareCard m={m} />
      </Container>
      <Container className="relative pb-6">
        <div className={compactClassName("flex flex-col gap-3 rounded-2xl border border-[#067647]/15 bg-[#f6fcf8] p-3 md:flex-row md:items-center")}>
          <p className={compactClassName("flex shrink-0 items-center gap-2 px-2 text-sm font-semibold text-[#05603a]")}>
            <TrendingDown aria-hidden="true" className="size-4" />
            {m.drops.label}
          </p>
          {/* min-w-0 on the list, each item and its title: a long title truncates inside its box instead of pushing the
              item past the panel's edge (owner 10/15/2026: "outside the box"). */}
          <ul className={compactClassName("grid min-w-0 flex-1 gap-2 md:grid-cols-3")}>
            {m.drops.items.map((d) => (
              <li key={d.href} className={compactClassName("min-w-0")}>
                <Item asChild size="sm" className="h-full min-w-0 bg-white">
                  <DestinationLink href={d.href}>
                    <ItemMedia>
                      <span className={compactClassName("rounded-full bg-[#ecfdf5] px-2 py-0.5 text-xs font-bold text-[#067647] ring-1 ring-[#067647]/15")}>{d.drop}</span>
                    </ItemMedia>
                    <ItemContent className="min-w-0 flex-1">
                      <ItemTitle className="block! w-auto! min-w-0 truncate text-[13px]!"><b className={compactClassName("font-semibold")}>{d.brand}</b> {d.title}</ItemTitle>
                      <ItemDescription className="text-xs!">· <b className={compactClassName("tabular font-semibold text-neutral-900")}>{d.price}</b> at {d.store}</ItemDescription>
                    </ItemContent>
                  </DestinationLink>
                </Item>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  )
}

/* ------------------------------------------------------------------ trust: live today, stats, press */
function CountUp({ value }: { value: string }) {
  // Animates the leading number once it scrolls into view; the server and first paint show the final text.
  const ref = React.useRef<HTMLSpanElement>(null)
  const reduced = useReducedMotion()
  React.useEffect(() => {
    const el = ref.current
    const m = value.match(/^([\d,.]+)(.*)$/)
    if (!el || !m || reduced) return
    const target = parseFloat(m[1].replace(/,/g, ''))
    const decimals = (m[1].split('.')[1] || '').length
    const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + m[2]
    let raf = 0
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return
      io.disconnect()
      const t0 = performance.now()
      const step = (t: number) => {
        const p = Math.min(1, (t - t0) / 1100)
        el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)))
        if (p < 1) raf = requestAnimationFrame(step)
        else el.textContent = value
      }
      raf = requestAnimationFrame(step)
    }, { threshold: 0.6 })
    io.observe(el)
    return () => { io.disconnect(); cancelAnimationFrame(raf) }
  }, [value, reduced])
  return <span ref={ref} data-countup="">{value}</span>
}

const TODAY_ICON = [TrendingDown, ShieldAlert, MapPin, Info]
const TODAY_TINT = ['bg-[#ecfdf5]! text-[#067647]!', 'bg-[#fef3f2]! text-[#b42318]!', 'bg-[#eef5ff]! text-[#0454c0]!', 'bg-[#fff4ec]! text-[#b54708]!']

export function Trust({ m }: { m: Model['trust'] }) {
  return (
    <section aria-label={m.aria} data-v3="V03" className={compactClassName("border-y border-neutral-200/70 bg-neutral-50/60 py-8 md:py-10")}>
      <Container>
        <div className={compactClassName("flex flex-col gap-4 lg:flex-row lg:items-stretch")}>
          <p className={compactClassName("flex shrink-0 items-center gap-2 text-sm font-semibold text-neutral-900 lg:w-28 lg:flex-col lg:items-start lg:justify-center")}>
            <span className={compactClassName("relative inline-flex size-2")}><span className={compactClassName("absolute inline-flex size-full animate-v3-ping rounded-full bg-[#10a65c] opacity-60")} data-motion /><span className={compactClassName("relative inline-flex size-2 rounded-full bg-[#10a65c]")} /></span>
            {m.todayLabel}
          </p>
          <ul className={compactClassName("grid flex-1 grid-cols-2 gap-3 lg:grid-cols-4")}>
            {m.today.map((t, k) => {
              const I = TODAY_ICON[k % 4]
              return (
                <li key={t.href}>
                  <Item asChild variant="outline" className="h-full bg-white transition hover:-translate-y-0.5 hover:shadow-md">
                    <DestinationLink href={t.href}>
                      <ItemMedia variant="icon" className={cn('size-10! rounded-xl! border-0!', TODAY_TINT[k % 4])}><I aria-hidden="true" className="size-5" /></ItemMedia>
                      <ItemContent>
                        <ItemTitle className="text-2xl! font-bold! leading-none! tracking-tight tabular"><CountUp value={t.num} /></ItemTitle>
                        <ItemDescription className="text-[13px]! leading-snug">{t.text}</ItemDescription>
                      </ItemContent>
                    </DestinationLink>
                  </Item>
                </li>
              )
            })}
          </ul>
        </div>
        <div className={compactClassName("mt-8 grid grid-cols-2 gap-x-5 gap-y-6 sm:grid-cols-3 lg:grid-cols-[repeat(6,1fr)_auto] lg:items-center")}>
          {m.stats.map((s) => (
            <div key={s.label} className={compactClassName("border-l-2 border-[#ff9951] pl-4")}>
              <p className={compactClassName("tabular text-2xl font-bold tracking-tight text-neutral-950 md:text-[1.7rem]")}><CountUp value={s.num} /></p>
              <p className={compactClassName("mt-1 text-[13px] text-neutral-600")}>{s.label}</p>
            </div>
          ))}
          <DestinationLink href={m.how.href} className="col-span-2 inline-flex min-h-8 items-center gap-1.5 text-sm font-semibold text-[#0454c0] hover:underline sm:col-span-3 lg:col-span-1">
            {words(m.how.text)}<ArrowRight aria-hidden="true" className="size-4" />
          </DestinationLink>
        </div>
        <div className={compactClassName("mt-8 flex flex-col items-center gap-4")}>
          <p className={compactClassName("flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500")}>
            <span aria-hidden="true" className={compactClassName("h-px w-10 bg-neutral-300")} />{m.pressLabel}<span aria-hidden="true" className={compactClassName("h-px w-10 bg-neutral-300")} />
          </p>
          {/* Press logos in their own colours (owner 10/15/2026: "these logos have their natural colors, just show it"). */}
          <Marquee label={m.pressAria} duration={70} className="w-full [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
            {m.press.map((p) => (
              <img key={p.src} src={p.src} alt={p.alt} loading="lazy" className={compactClassName("mx-7 h-7 w-auto max-w-[140px] object-contain md:h-8")} />
            ))}
          </Marquee>
        </div>
      </Container>
    </section>
  )
}
